import type { Locale } from "@/i18n/locales";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";

/**
 * One category's plan for one period (`model.md` §3.6).
 *
 * There is no currency: a budget is always in the household's base currency, and
 * `amount_minor` is a positive magnitude rather than a signed amount — no money
 * moves in a budget row, so the sign convention of §2.2 rule 1 does not apply.
 */
export interface Budget {
  id: string;
  category_id: string;
  period_type: string;
  period_start: string;
  period_end: string;
  amount_minor: number;
  note?: string;
}

/** A budget beside what actually happened (`model.md` §4.2). */
export interface BudgetActual {
  budget: Budget;
  /** §4.2's sum as the model defines it: signed, base currency, descendants included. */
  actual_minor: number;
  /** The same figure as progress against the limit, counting upward. */
  spent_minor: number;
  remaining_minor: number;
  used_ratio: number;
  /** The only thing `project.md` §5.5 lets this screen colour red. */
  is_overspent: boolean;
  lines: number;
}

export interface PeriodActuals {
  period_start: string;
  period_end: string;
  budgets: BudgetActual[];
  /** Spending no budget covers — the number a budget screen cannot get from any budget. */
  uncategorised_minor: number;
  uncategorised_lines: number;
}

/** A proposed amount, computed on demand and never stored (`project.md` §5.4). */
export interface BudgetSuggestion {
  category_id: string;
  amount_minor: number;
  /** The middle month before rounding, so the screen can say where the number came from. */
  typical_minor: number;
  spent_last_month_minor: number;
  months: number;
}

interface List<T> {
  items: T[];
}

/** The first day of the month containing a date, as the API's period key. */
export function monthOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
}

/**
 * Splits a period key into its year and month.
 *
 * The parts are read positionally rather than destructured, because a key that
 * is not `YYYY-MM-DD` would otherwise become a `Date` built from `undefined` —
 * an Invalid Date that renders as a blank heading instead of failing.
 */
function partsOf(period: string): { year: number; month: number } {
  const parts = period.split("-");
  return { year: Number(parts[0] ?? 0), month: Number(parts[1] ?? 1) };
}

/** Shifts a period key by whole months. */
export function shiftMonth(period: string, by: number): string {
  const { year, month } = partsOf(period);
  return monthOf(new Date(year, month - 1 + by, 1));
}

/**
 * A period key as a month name, for a heading.
 *
 * The locale is the reader's (K-36): a budget screen headed "March 2026" for a
 * British reader and "März 2026" for a German one, over the same data.
 */
export function monthLabel(period: string, locale: Locale): string {
  const { year, month } = partsOf(period);
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(
    new Date(year, month - 1, 1),
  );
}

export const budgetsQueryKey = (householdId: string, period: string) =>
  ["households", householdId, "budgets", period] as const;

export function useBudgetActuals(householdId: string | undefined, period: string) {
  return useQuery({
    queryKey: [...budgetsQueryKey(householdId ?? "", period), "actuals"],
    queryFn: ({ signal }) =>
      apiFetch<PeriodActuals>(
        `/api/v1/households/${householdId}/budgets/actuals?period=${period}`,
        { signal },
      ),
    enabled: Boolean(householdId),
  });
}

export function useBudgetSuggestions(householdId: string | undefined, period: string) {
  return useQuery({
    queryKey: [...budgetsQueryKey(householdId ?? "", period), "suggestions"],
    queryFn: ({ signal }) =>
      apiFetch<List<BudgetSuggestion>>(
        `/api/v1/households/${householdId}/budgets/suggestions?period=${period}`,
        { signal },
      ),
    enabled: Boolean(householdId),
  });
}

export interface CreateBudgetInput {
  category_id: string;
  period: string;
  amount_minor: number;
}

export function useCreateBudget(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBudgetInput) =>
      apiFetch<Budget>(`/api/v1/households/${householdId}/budgets`, {
        method: "POST",
        body: input,
      }),
    // Both queries: a new budget changes the bars *and* removes itself from the
    // suggestions, and accepting one can make its parent or children ineligible
    // (`model.md` §3.6).
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["households", householdId, "budgets"] }),
  });
}

export function useDeleteBudget(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (budgetId: string) =>
      apiFetch<void>(`/api/v1/households/${householdId}/budgets/${budgetId}`, {
        method: "DELETE",
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["households", householdId, "budgets"] }),
  });
}
